import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { throttle } from 'lodash';

import { noop } from 'utils/func';
import { classIfItemIsChosen, isVideo } from 'apps/products/utils/bopGallery';

import { getProductImageUrl, IMAGE_SIZES } from 'utils/productImages';
import { getPassiveHandlerDefaultOptions } from 'utils/detectPassiveEvents';

const NAVIGATE_RIGHT = 1;
const NAVIGATE_LEFT = -1;

export default class BopGalleryMiniatures extends PureComponent {
  static propTypes = {
    isDesktopBreakpoint: PropTypes.bool.isRequired,
    items: PropTypes.array.isRequired,
    onClick: PropTypes.func,
    shownItemIndex: PropTypes.number.isRequired,
    sku: PropTypes.string.isRequired,
  };

  static defaultProps = {
    onClick: noop,
  };

  state = {
    canNavigateLeft: false,
    canNavigateRight: false,
    shouldShowArrows: false,
    offset: 0,
  };

  componentDidMount() {
    window.addEventListener('resize', this.handleResize, getPassiveHandlerDefaultOptions());
    this.updateNavigationState();
  }

  componentDidUpdate() {
    if (this.state.shouldShowArrows !== this.shouldShowArrows()) {
      this.updateNavigationState(this.state.offset);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize, getPassiveHandlerDefaultOptions());
  }

  navigationRef = React.createRef();

  miniaturesRef = React.createRef();

  navDirection = NAVIGATE_RIGHT;

  get calculatedStyles() {
    if (this.props.isDesktopBreakpoint) {
      return {
        top: this.state.offset,
      };
    }

    return {
      left: this.state.offset,
    };
  }

  updateNavigationState = (offset = 0, position = this.props.shownItemIndex, callback = noop()) => {
    this.setState(() => ({
      shouldShowArrows: this.shouldShowArrows(),
      canNavigateLeft: position > 0,
      canNavigateRight: !this.isLastVisible(offset),
      offset,
    }), callback);
  };

  isLastVisible = (offset) => {
    const navigation = this.navigationRef.current;
    const miniatures = this.miniaturesRef.current;

    return this.getElementOffsetSize(navigation) >= this.getElementOffsetSize(miniatures) + offset;
  };

  getElementOffsetSize = (element) => {
    if (!element) {
      return 0;
    }

    return this.props.isDesktopBreakpoint ? element.offsetHeight : element.offsetWidth;
  };

  getMaxOffset = () =>
    this.getElementOffsetSize(this.miniaturesRef.current) - this.getElementOffsetSize(this.navigationRef.current);

  getMiniatureSize = () => Math.round(this.getElementOffsetSize(this.miniaturesRef.current) / this.props.items.length);

  handleResize = throttle(() => {
    let offset = 0;

    if (this.shouldShowArrows()) {
      const { shownItemIndex } = this.props;

      const arrowOffset = this.getElementOffsetSize(this.navigationRef.current.querySelector('.icon'));
      offset = -Math.min(
        this.getMaxOffset(),
        shownItemIndex * this.getMiniatureSize() - (shownItemIndex ? arrowOffset : 0),
      );
    }

    this.updateNavigationState(offset);
  }, 100);

  isThresholdPositionReached = (position) => {
    const offsetSize = this.getElementOffsetSize(this.navigationRef.current);

    if (this.navDirection === NAVIGATE_RIGHT) {
      const thresholdPosition = Math.round(offsetSize / this.getMiniatureSize()) - 1;
      return position >= thresholdPosition;
    }

    const thresholdPosition = this.props.items.length - (Math.round(offsetSize / this.getMiniatureSize()));
    return position <= thresholdPosition;
  };

  shouldShowArrows = () =>
    this.getElementOffsetSize(this.miniaturesRef.current) > this.getElementOffsetSize(this.navigationRef.current);

  switchNavigationDirection = (position) => {
    if (position > this.props.shownItemIndex) {
      this.navDirection = NAVIGATE_RIGHT;
      return;
    }

    this.navDirection = NAVIGATE_LEFT;
  };

  onClick = (position) => {
    const { onClick } = this.props;
    const callback = () => onClick(position);

    if (this.shouldShowArrows()) {
      let { offset } = this.state;

      this.switchNavigationDirection(position);
      if (this.isThresholdPositionReached(position)) {
        offset -= this.navDirection * this.getMiniatureSize();
      }

      this.updateNavigationState(
        -Math.min(this.getMaxOffset(), Math.abs(Math.min(offset, 0))),
        position,
        callback
      );
    } else {
      this.updateNavigationState(0, position, callback);
    }
  };

  onArrowClick = (navDirection) => () => {
    this.onClick(this.props.shownItemIndex + navDirection);
  };

  renderMiniatures = () => {
    const { isDesktopBreakpoint, items, shownItemIndex, sku } = this.props;
    const { canNavigateLeft, canNavigateRight, shouldShowArrows } = this.state;

    return (
      <>
        {shouldShowArrows && canNavigateLeft && (
          <div // eslint-disable-line
            className={classNames(
              'icon',
              { 'icon__chevron-up': isDesktopBreakpoint },
              { 'icon__chevron-left': !isDesktopBreakpoint },
            )}
            onClick={this.onArrowClick(NAVIGATE_LEFT)}
          />
        )}
        <div
          className="bop-gallery__miniatures"
          ref={this.miniaturesRef}
          style={this.calculatedStyles}
        >
          {
          items.map(
            (item, index) => {
              const key = `bop-gallery__mini__${index}`;

              if (isVideo(item)) {
                return (
                  <span // eslint-disable-line
                    onClick={() => this.onClick(index)}
                    key={key}
                    className={classNames(
                      'bop-gallery__miniature',
                      'bop-gallery__playVideo',
                      classIfItemIsChosen(shownItemIndex, index, 'miniature'),
                    )}
                  />
                );
              }

              return (
                <img // eslint-disable-line
                  onClick={() => this.onClick(index)}
                  key={key}
                  className={
                    `bop-gallery__miniature ${classIfItemIsChosen(shownItemIndex, index, 'miniature')}`
                  }
                  src={getProductImageUrl(sku, item, IMAGE_SIZES.S_75)}
                  alt={item.name}
                />
              );
            }
          )
        }
        </div>
        {shouldShowArrows && canNavigateRight && (
          <div // eslint-disable-line
            className={classNames(
              'icon',
              { 'icon__chevron-down': isDesktopBreakpoint },
              { 'icon__chevron-right': !isDesktopBreakpoint },
            )}
            onClick={this.onArrowClick(NAVIGATE_RIGHT)}
          />
        )}
      </>
    );
  };

  renderDots = () => {
    const { items, onClick, shownItemIndex } = this.props;
    return (
      <ul className="bop-gallery__navigation__dots">
        {items.map(
          (item, index) => ((
            /* eslint-disable */
            <li
              onClick={() => onClick(index)}
              key={`bop-gallery__dot__${index}`}
              className={`bop-gallery__dot ${classIfItemIsChosen(shownItemIndex, index, 'dot')}`}
            />
            /* eslint-enable */
          )),
        )}
      </ul>);
  };

  render() {
    const numberOfItems = this.props.items.length;
    if (!numberOfItems || numberOfItems < 2) {
      return null;
    }

    return (
      <div
        className="bop-gallery__navigation"
        ref={this.navigationRef}
      >
        {this.renderMiniatures()}
        {this.renderDots()}
      </div>
    );
  }
}
